import { useMemo } from 'react';
import type { KeyPrefix, TFunction } from 'i18next';
// eslint-disable-next-line no-restricted-imports -- this is the wrapper R9 points everyone else to
import { useTranslation as useI18nextTranslation } from 'react-i18next';
import type { FallbackNs, UseTranslationOptions, UseTranslationResponse } from 'react-i18next';

// The context module, not the barrel: the provider pulls in auth and React Query.
import { usePlatformEdition } from 'src/context/platform-edition-context/platform-edition-context';
import { EDITION_I18N_CONTEXT } from 'src/utils/platform-features';

type NamespaceArg = Parameters<typeof useI18nextTranslation>[0];
type TOptions = Record<string, unknown>;

/** Adds the edition's context unless the call site set its own (`<Trans>` passes `context: undefined`). */
const withContext = (options: unknown, context: string): TOptions => {
  const own = (options ?? {}) as TOptions;
  return own.context === undefined ? { ...own, context } : own;
};

const bindContext = (t: TFunction, context: string) => {
  const call = t as unknown as (...args: unknown[]) => unknown;
  // `t(key, defaultValue, options)` and `t(key, options)` are both valid i18next calls.
  return ((key: unknown, second?: unknown, third?: unknown) =>
    typeof second === 'string'
      ? call(key, second, withContext(third, context))
      : call(key, withContext(second, context))) as unknown as TFunction;
};

/**
 * `useTranslation` from react-i18next, plus the platform edition's wording: on a corporate
 * platform every `t('key')` reads `key_corporate` when it exists and `key` otherwise. Call
 * sites stay unchanged — the variant lives in the locale file only. On an edition without a
 * context the react-i18next response is returned untouched.
 */
export function useTranslation<
  Ns extends NamespaceArg = undefined,
  KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined,
>(
  ns?: Ns,
  options?: UseTranslationOptions<KPrefix>
): UseTranslationResponse<FallbackNs<Ns>, KPrefix> {
  const response = useI18nextTranslation(ns, options);
  const context = EDITION_I18N_CONTEXT[usePlatformEdition()];
  const { t, i18n, ready } = response;

  const contextualT = useMemo(
    () => (context ? (bindContext(t as TFunction, context) as typeof t) : t),
    [t, context]
  );

  return useMemo(() => {
    if (contextualT === t) return response;
    return Object.assign([contextualT, i18n, ready], {
      t: contextualT,
      i18n,
      ready,
    }) as typeof response;
    // `response` is a fresh array on every render; its parts are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextualT, t, i18n, ready]);
}
